import Filters, { FilterComponent } from "@/components/Filters"
import PermissionGuard from "@/components/PermissionGuard"
import { PERMISSIONS } from "@/constants/Common"
import {
  confirmCashFlow,
  createCashFlow,
  deleteCashFlow,
  fetchCashFlows,
  unconfirmCashFlow,
} from "@/modules/finances/cash-flows/services/cash-flows.services"
import { CashFlow, CashFlowstatus, DataCashFlow, MAP_MONEY_SOURCE_STATUS } from "@/modules/finances/cash-flows/types"
import UpsertForm from "@/modules/finances/cash-flows/views/UpsertForm"
import { formatMoney, normalizeText } from "@/utils"
import { CheckCircleOutlined, CloseCircleOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons"
import { Button, Card, Flex, Modal, Space, Table, Tag, Tooltip, Typography, message } from "antd"
import { SorterResult } from "antd/es/table/interface"
import dayjs from "dayjs"
import queryString from "query-string"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { AnyObject } from "yup/lib/types"

type EditableTableProps = Parameters<typeof Table>[0]
type ColumnTypes = Exclude<EditableTableProps["columns"], undefined>

const List: React.FC = () => {
  const [dataSource, setDataSource] = useState<DataCashFlow>()
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const navigate = useNavigate()
  const location = useLocation()
  const filterRef = useRef<{ filterFunction: () => void } | null>(null)
  const [filterValues, setFilterValues] = useState<{ [key: string]: string } | null>(null)
  const [sortPaginationValues, setSortPaginationValues] = useState<{ [key: string]: string } | null>(null)
  const [defaultFilterValues, setDefaultFilterValues] = useState<{ [key: string]: any } | null>(null)
  const [open, setOpen] = useState(false)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [CashFlowToDelete, setCashFlowToDelete] = useState<any>(null)
  const [confirmModalVisible, setConfirmModalVisible] = useState(false)
  const [unconfirmModalVisible, setUnconfirmModalVisible] = useState(false)
  const [CashFlowToConfirm, setCashFlowToConfirm] = useState<any>(null)
  const [CashFlowToUnconfirm, setCashFlowToUnconfirm] = useState<any>(null)

  const onRefresh = () => {
    if (filterRef.current) {
      filterRef.current.filterFunction()
    }
    setSortPaginationValues(null)
  }

  const onDismissModal = () => {
    setOpen(false)
  }

  const showModal = () => {
    setOpen(true)
  }

  const showModalEdit = (id: string) => {
    navigate(`/finances/cash-flows/${id}${location.search}`)
  }

  const handleCreate = async (data: any) => {
    try {
      const response = await createCashFlow(data)

      if (response?.statusCode === 400) {
        message.error(response.message || "Có lỗi xảy ra khi tạo báo cáo dòng tiền!")
        return
      }

      if (response?.data) {
        message.success("Tạo báo cáo dòng tiền thành công!")
        onDismissModal()
        fetchData()
      }
    } catch (error: any) {
      if (error?.data?.message) {
        message.error(error.data.message)
      } else {
        message.error("Có lỗi xảy ra khi tạo báo cáo dòng tiền!")
      }
    }
  }

  const handleDeleteCashFlowClick = (record: CashFlow) => {
    setCashFlowToDelete(record)
    setDeleteModalVisible(true)
  }

  const handleDeleteCashFlow = async () => {
    if (!CashFlowToDelete) return

    try {
      await (deleteCashFlow as any)(CashFlowToDelete)
      message.success("Xóa báo cáo nguồn tiền thành công!")
      setDeleteModalVisible(false)
      setCashFlowToDelete(null)
      fetchData()
    } catch (error: any) {
      if (error?.data?.message) {
        message.error(error.data.message)
      } else {
        message.error("Có lỗi xảy ra khi xóa báo cáo nguồn tiền!")
      }
    }
  }

  const handleConfirmCashFlowClick = (record: CashFlow) => {
    setCashFlowToConfirm(record)
    setConfirmModalVisible(true)
  }

  const handleConfirmCashFlow = async () => {
    if (!CashFlowToConfirm) return

    try {
      await (confirmCashFlow as any)(String(CashFlowToConfirm.id))
      message.success("Xác nhận báo cáo dòng tiền thành công!")
      setConfirmModalVisible(false)
      setCashFlowToConfirm(null)
      fetchData()
    } catch (error: any) {
      if (error?.response?.data?.message) {
        message.error(error.response.data.message)
      } else {
        message.error("Có lỗi xảy ra khi xác nhận báo cáo dòng tiền!")
      }
    }
  }

  const handleUnconfirmCashFlowClick = (record: CashFlow) => {
    setCashFlowToUnconfirm(record)
    setUnconfirmModalVisible(true)
  }

  const handleUnconfirmCashFlow = async () => {
    if (!CashFlowToUnconfirm) return

    try {
      await (unconfirmCashFlow as any)(String(CashFlowToUnconfirm.id))
      message.success("Hủy xác nhận báo cáo dòng tiền thành công!")
      setUnconfirmModalVisible(false)
      setCashFlowToUnconfirm(null)
      fetchData()
    } catch (error: any) {
      if (error?.response?.data?.message) {
        message.error(error.response.data.message)
      } else {
        message.error("Có lỗi xảy ra khi hủy xác nhận báo cáo dòng tiền!")
      }
    }
  }

  const onTableChanged = ((pagination, _, sorter: SorterResult<AnyObject>) => {
    const sortOptions: { [key: string]: string } = sorter.order
      ? { [sorter.field as string]: sorter.order === "descend" ? "desc" : "asc" }
      : {}
    const paginationOptions: { [key: string]: string } = {
      page: `${pagination.current}`,
      limit: `${pagination.pageSize}`,
    }

    if (pagination?.current) {
      setCurrentPage(pagination.current)
    }

    setSortPaginationValues({ ...sortOptions, ...paginationOptions })
    const searchParams = queryString.stringify({ ...filterValues, ...sortOptions, ...paginationOptions })

    navigate(searchParams.length > 0 ? `${location.pathname}?${searchParams}` : location.pathname)
  }) as EditableTableProps["onChange"]

  const defaultColumns: (ColumnTypes[number] & { editable?: boolean; dataIndex: string })[] = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: "5%",
      align: "right",
      render: (id: number) => id,
    },
    {
      title: "Ngày báo cáo",
      dataIndex: "date",
      key: "date",
      width: "8%",
      render: date => date && dayjs(new Date(date)).format("DD/MM/YYYY"),
      sorter: true,
      align: "right",
      sortDirections: ["descend", "ascend", null],
      sortOrder: (() => {
        if (!sortPaginationValues?.date) return null
        return sortPaginationValues.date === "asc" ? "ascend" : "descend"
      })(),
    },
    {
      title: "Doanh thu sổ sách",
      dataIndex: "book_revenue",
      key: "book_revenue",
      width: "13%",
      align: "right",
      render: amount => `${formatMoney(amount)} đ`,
      sorter: true,
      sortDirections: ["descend", "ascend", null],
      sortOrder: (() => {
        if (!sortPaginationValues?.book_revenue) return null
        return sortPaginationValues.book_revenue === "asc" ? "ascend" : "descend"
      })(),
    },
    {
      title: "Tiền mặt",
      dataIndex: "cash_amount",
      key: "cash_amount",
      width: "13%",
      align: "right",
      render: amount => `${formatMoney(amount)} đ`,
      sorter: true,
      sortDirections: ["descend", "ascend", null],
      sortOrder: (() => {
        if (!sortPaginationValues?.cash_amount) return null
        return sortPaginationValues.cash_amount === "asc" ? "ascend" : "descend"
      })(),
    },
    {
      title: "Chuyển khoản",
      dataIndex: "bank_transfer_amount",
      key: "bank_transfer_amount",
      width: "13%",
      align: "right",
      render: amount => `${formatMoney(amount)} đ`,
      sorter: true,
      sortDirections: ["descend", "ascend", null],
      sortOrder: (() => {
        if (!sortPaginationValues?.bank_transfer_amount) return null
        return sortPaginationValues.bank_transfer_amount === "asc" ? "ascend" : "descend"
      })(),
    },

    {
      title: "Chênh lệch",
      dataIndex: "difference_amount",
      key: "difference_amount",
      width: "13%",
      align: "right",
      render: differenceAmount => {
        const isNegative = differenceAmount > 0
        const isPositive = differenceAmount < 0

        return (
          <span
            style={{
              color: isNegative ? "#ff4d4f" : isPositive ? "#52c41a" : "#000000",
              fontWeight: "bold",
            }}
          >
            {formatMoney(-differenceAmount)} đ
          </span>
        )
      },
      sorter: true,
      sortDirections: ["descend", "ascend", null],
      sortOrder: (() => {
        if (!sortPaginationValues?.difference_amount) return null
        return sortPaginationValues.difference_amount === "asc" ? "ascend" : "descend"
      })(),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      width: "8%",
      key: "status",
      render: (status: CashFlowstatus, record) => {
        if (record.is_confirmed) {
          return <Tag color="success">Đã xác nhận</Tag>
        }

        return <Tag color={status === CashFlowstatus.OK ? "success" : "error"}>{MAP_MONEY_SOURCE_STATUS[status]}</Tag>
      },
    },
    {
      title: "Ghi chú",
      dataIndex: "notes",
      key: "notes",
      width: "8%",
      render: notes => notes || "-",
    },
    {
      title: "Thao tác",
      dataIndex: "operation",
      width: "17%",
      render: (_, record) =>
        dataSource?.items && dataSource.items.length >= 1 ? (
          <Space size="small">
            <PermissionGuard permission={PERMISSIONS.CASH_FLOWS_CONFIRM}>
              {!record.is_confirmed ? (
                <Tooltip title="Xác nhận báo cáo">
                  <Button
                    type="default"
                    shape="round"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    style={{ color: "#52c41a" }}
                    onClick={() => handleConfirmCashFlowClick(record as CashFlow)}
                  >
                    Xác nhận
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip title="Hủy xác nhận">
                  <Button
                    type="default"
                    shape="round"
                    size="small"
                    icon={<CloseCircleOutlined />}
                    style={{ color: "#ff4d4f" }}
                    onClick={() => handleUnconfirmCashFlowClick(record as CashFlow)}
                  >
                    Hủy xác nhận
                  </Button>
                </Tooltip>
              )}
            </PermissionGuard>
            <PermissionGuard permission={PERMISSIONS.CASH_FLOWS_UPDATE}>
              <Button
                ghost
                type="primary"
                shape="round"
                size="small"
                onClick={() => showModalEdit(String(record?.id))}
                disabled={record.is_confirmed}
              >
                Sửa
              </Button>
            </PermissionGuard>

            <PermissionGuard permission={PERMISSIONS.CASH_FLOWS_DELETE}>
              <Button
                type="default"
                shape="round"
                danger
                size="small"
                onClick={() => handleDeleteCashFlowClick(record as CashFlow)}
                disabled={record.is_confirmed}
              >
                Xóa
              </Button>
            </PermissionGuard>
          </Space>
        ) : null,
    },
  ]

  const columns = defaultColumns.map(col => {
    if (!col.editable) {
      return col
    }
    return {
      ...col,
      onCell: (record: CashFlow) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
      }),
    }
  })

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const searchParams = queryString.parse(location.search)
      const response = await fetchCashFlows(searchParams)
      if (response?.data) {
        setDataSource(response.data)
      }
    } catch (error) {
      console.error("Error fetching money sources:", error)
    } finally {
      setLoading(false)
    }
  }, [location.search])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    const searchParams = queryString.parse(location.search)
    const filterValues: { [key: string]: any } = {}
    const sortPaginationValues: { [key: string]: string } = {}
    for (const key of Object.keys(searchParams)) {
      const value = searchParams[key]
      if (value && typeof value === "string") {
        if (key === "from_date" || key === "to_date") {
          filterValues[key] = dayjs(value)
          continue
        }
        if (key === "page" || key === "limit" || value === "asc" || value === "desc") {
          if (key === "page") {
            setCurrentPage(parseInt(value))
          }
          sortPaginationValues[key] = value
        } else {
          filterValues[key] = value
        }
      }
    }

    setFilterValues(filterValues)
    setSortPaginationValues(sortPaginationValues)
    setDefaultFilterValues({ ...filterValues, ...sortPaginationValues })
  }, [location.search])

  const onFilterChange = (values: object) => {
    let newValue = { ...values, ...sortPaginationValues }

    if (newValue.search) {
      newValue = { ...newValue, search: normalizeText(newValue.search) }
    }

    if (newValue.from_date) {
      newValue = { ...newValue, from_date: dayjs(newValue.from_date).format("YYYY-MM-DD") }
    }

    if (newValue.to_date) {
      newValue = { ...newValue, to_date: dayjs(newValue.to_date).format("YYYY-MM-DD") }
    }

    setFilterValues(newValue as { [key: string]: string })
    setSortPaginationValues(null)
    const searchParams = queryString.stringify(newValue)
    navigate(searchParams.length > 0 ? `${location.pathname}?${searchParams}` : location.pathname)
  }

  return (
    <Card size="small">
      <Flex justify="space-between" style={{ flexWrap: "wrap" }}>
        <Typography.Title level={4} style={styles.title}>
          Danh sách Báo cáo dòng tiền
        </Typography.Title>
        <Space style={{ flexWrap: "wrap", marginTop: "10px" }}>
          <Button onClick={onRefresh} icon={<ReloadOutlined />}>
            Làm mới
          </Button>
          <PermissionGuard permission={PERMISSIONS.CASH_FLOWS_CREATE}>
            <Button type="primary" onClick={() => showModal()} icon={<PlusOutlined />}>
              Thêm mới
            </Button>
          </PermissionGuard>
        </Space>
      </Flex>
      <Card type="inner" size="small" style={styles.card}>
        <Filters
          filterRef={filterRef}
          defaultFilterValues={defaultFilterValues}
          onSubmit={onFilterChange}
          filters={
            [
              {
                label: "Lọc theo ngày từ",
                placeholder: "Vui lòng chọn ngày từ",
                name: "from_date",
                type: "date",
                defaultValue: "",
              },
              {
                label: "đến",
                placeholder: "Vui lòng chọn ngày đến",
                name: "to_date",
                type: "date",
                defaultValue: "",
              },
              {
                label: "Lọc trạng thái",
                placeholder: "Vui lòng chọn trạng thái",
                name: "status",
                type: "select",
                options: [
                  { value: "", label: "Tất cả" },
                  { value: CashFlowstatus.OK, label: "Khớp" },
                  { value: CashFlowstatus.DIFFERENCE, label: "Không khớp" },
                ],
                defaultValue: "",
              },
              {
                label: "Tìm kiếm",
                placeholder: "Tìm kiếm theo ghi chú",
                name: "notes",
                type: "text",
                defaultValue: "",
              },
            ] as FilterComponent[]
          }
        />
      </Card>
      <UpsertForm visible={open} onCancel={onDismissModal} onSubmit={handleCreate} loading={loading} mode="create" />

      <Modal
        title="Xác nhận xóa báo cáo dòng tiền"
        open={deleteModalVisible}
        onOk={handleDeleteCashFlow}
        onCancel={() => {
          setDeleteModalVisible(false)
          setCashFlowToDelete(null)
        }}
        okText="Xóa"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
      >
        <p>Bạn có chắc chắn muốn xóa báo cáo dòng tiền này?</p>
        <p style={{ color: "#ff4d4f", fontWeight: "bold" }}>Hành động này không thể hoàn tác!</p>
      </Modal>

      <Modal
        title="Xác nhận báo cáo nguồn tiền"
        open={confirmModalVisible}
        onOk={handleConfirmCashFlow}
        onCancel={() => {
          setConfirmModalVisible(false)
          setCashFlowToConfirm(null)
        }}
        okText="Xác nhận"
        cancelText="Hủy"
        okButtonProps={{ style: { backgroundColor: "#52c41a", borderColor: "#52c41a" } }}
      >
        <p>Bạn có chắc chắn muốn xác nhận báo cáo dòng tiền này?</p>
      </Modal>

      <Modal
        title="Hủy xác nhận báo cáo nguồn tiền"
        open={unconfirmModalVisible}
        onOk={handleUnconfirmCashFlow}
        onCancel={() => {
          setUnconfirmModalVisible(false)
          setCashFlowToUnconfirm(null)
        }}
        okText="Hủy xác nhận"
        cancelText="Đóng"
        okButtonProps={{ danger: true }}
      >
        <p>Bạn có chắc chắn muốn hủy xác nhận báo cáo dòng tiền này?</p>
      </Modal>

      <Table
        rowClassName={() => "editable-row"}
        bordered
        dataSource={dataSource?.items}
        columns={columns as ColumnTypes}
        loading={loading}
        scroll={{ y: 400 }}
        onChange={onTableChanged}
        pagination={{
          total: dataSource?.meta?.total,
          pageSize: dataSource?.meta?.limit,
          current: currentPage,
          responsive: true,
        }}
        rowKey={record => record.id}
      />
    </Card>
  )
}

export default List

const styles = {
  title: {
    marginTop: 0,
    marginBottom: 0,
  },
  card: {
    marginTop: "1.25rem",
    marginBottom: "1.25rem",
  },
}
